import { ValidationError } from '@goodt-common/layers';
import { Validator as JsonSchemaValidator } from 'jsonschema';
import { success, fail } from '@goodt-common/utils';
import datasetPropsScheme from './dataset-props-schema.json';
import { DATASET_REQUEST_PROPS_NAME_MAX_LENGTH, ToastMessage, ValidationErrorMessage } from '../const';
import { throwGlobal } from '@goodt-wcore/errors';

/**
 * @typedef {import('@goodt-common/data').IDatasetRequestProps} IDatasetRequestProps
 * @typedef {import('jsonschema').ValidationError} SchemaValidationError
 */

export class DatasetJsonValidator {
    model;

    constructor(json) {
        this.json = json;
    }

    /**
     * @param {IDatasetRequestProps} datasetRequest
     * @return {SafeResult<IDatasetRequestProps, ValidationError>}
     * @private
     */
    _validateName(datasetRequest) {
        const { name }  = datasetRequest;
        if (name?.length === 0) {
            return fail(new ValidationError(ValidationErrorMessage.DATASET_PROPS_NAME_REQUIRED, {
                property: 'name'
            }));
        }
        if (name?.length > DATASET_REQUEST_PROPS_NAME_MAX_LENGTH) {
            return fail(new ValidationError(ValidationErrorMessage.DATASET_PROPS_NAME_TOO_LONG, {
                property: 'name'
            }));
        }
        return success(datasetRequest);
    }

    /**
     * @param {IDatasetRequestProps} json
     * @return {SafeResult<IDatasetRequestProps, ValidationError>}
     * @private
     */
    _validateSemantic(json) {
        const validator = new JsonSchemaValidator();
        const { valid, errors } = validator.validate(json, datasetPropsScheme, {
            base: 'file:///schemas/'
        });
        if (valid) {
            return success(json);
        }
        throwGlobal(errors[0]);
        return fail(new ValidationError(ToastMessage.IMPORT_FROM_CLIPBOARD_INVALID_SCHEMA(), {
            property: null
        }));
    }

    /**
     * @return {SafeResult<IDatasetRequestProps, ValidationError | null>}
     * @private
     */
    _validateIsJson() {
        try {
            const requestProps = JSON.parse(this.json);
            return success(requestProps);
        } catch {
            return fail(new ValidationError(ToastMessage.IMPORT_FROM_CLIPBOARD_INVALID_JSON(), {
                property: null
            }));
        }
    }

    /**
     * @return {SafeResult<IDatasetRequestProps, ValidationError|SchemaValidationError>}
     */
    validate() {
        let safeResult = this._validateIsJson();
        if (safeResult.isError) {
            return safeResult;
        }
        safeResult = this._validateSemantic(safeResult.result);
        if (safeResult.isError) {
            return safeResult;
        }
        return this._validateName(safeResult.result);
    }
}
